import { router } from 'expo-router';

import { routes } from '@/constants/routes';
import type {
  AuthApiError as ApiError,
  AuthenticationResponse,
} from '@/types/auth';

export type { ApiError };

export type DisabledAccountUser = Pick<
  AuthenticationResponse['user'],
  'id' | 'enabled' | 'disabledAt' | 'customer'
>;

export type DisabledAccountPii = {
  fullName: string;
  email: string;
  phone: string;
  photoUrl: string;
};

// Module-level store to avoid passing PII in URL params/referrers/analytics
let _disabledAccountPii: DisabledAccountPii | null = null;

export function getDisabledAccountPii(): DisabledAccountPii | null {
  return _disabledAccountPii;
}

export function clearDisabledAccountPii(): void {
  _disabledAccountPii = null;
}

/**
 * Navigates to the account-disabled screen when the signed-in user is disabled.
 * Returns true if navigation occurred (caller should return early).
 * Skips token storage, invalidation, and post-auth callbacks for disabled users.
 */
export function navigateToDisabledAccount(user: DisabledAccountUser): boolean {
  if (user.enabled !== false) return false;
  const { customer } = user;
  _disabledAccountPii = {
    fullName: [customer.firstName, customer.lastName].filter(Boolean).join(' '),
    email: customer.email ?? '',
    phone: customer.phone ?? '',
    photoUrl: customer.photos?.[0]?.url ?? '',
  };
  router.replace({
    pathname: routes.accountDisabled,
    params: {
      userId: user.id,
      disabledAt: user.disabledAt ? String(user.disabledAt) : '',
    },
  });
  return true;
}

export function extractApiError(
  error: unknown,
  fallbackCode: string,
): ApiError {
  if (error instanceof Error) {
    const e = error as Error & { code?: unknown; details?: unknown };
    return {
      message: e.message,
      code: typeof e.code === 'string' ? e.code : fallbackCode,
      details: e.details,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const e = error as Record<string, unknown>;
    const hasMessage = typeof e.message === 'string';
    const hasCode = typeof e.code === 'string';
    // The API client rejects with the raw response body, and some are keyed
    // only by `code` with no `message` (e.g. the standby duplicate-join 409:
    // `{ code, entryId, statusUrl }`). Bailing to the generic fallback there
    // dropped the real code, so a caller mapping on it lost the reason. Keep
    // whichever of code/details the body carries.
    if (hasMessage || hasCode || 'details' in e) {
      return {
        message: hasMessage ? (e.message as string) : 'Unknown error',
        code: hasCode ? (e.code as string) : fallbackCode,
        details: e.details,
      };
    }
  }

  return { message: 'Unknown error', code: fallbackCode, details: undefined };
}
